import {useCallback} from "react";
import {useTranslation} from "react-i18next";
import {useNavigate} from "react-router-dom";
import {useAuthStore} from "@/store/useAuth.store";
import {useMiniGames} from "@/store/useMiniGames";
import {SLOTS_GAMES_NAVIGATION} from "@/util/categoryDisplayName";
import styles from "./CategoryPills.module.css";

const GAME_CATEGORIES = [
  {
    id: "slots",
    labelKey: "categorySlots",
    defaultLabel: "Slots",
    icon: "🎰",
    slotsNavigation: true,
  },
  {id: "live-casino", labelKey: "categoryLiveCasino", defaultLabel: "Casino en Vivo", icon: "🃏", path: "/casino"},
  {id: "sports", labelKey: "categorySports", defaultLabel: "Deportes", icon: "⚽", path: "/sports"},
  {id: "horses", labelKey: "categoryHorses", defaultLabel: "Caballos", icon: "🏇", path: "/horses"},
  {id: "bingo", labelKey: "categoryBingo", defaultLabel: "Bingo", icon: "🎯", path: "/bingo"},
  {id: "mini-games", labelKey: "categoryMiniGames", defaultLabel: "Mini Juegos", icon: "🕹️", path: "/mini-juegos"},
  {id: "animalitos", labelKey: "categoryAnimalitos", defaultLabel: "Lotería", icon: "🎫", path: "/lottery"},
  {id: "plinko", labelKey: "categoryPlinko", defaultLabel: "Plinko", icon: "🔴", path: "/games/category/plinko"},
  {id: "esports", labelKey: "categoryEsports", defaultLabel: "E-Sports", icon: "🎮", path: "/games/pragmatic/vplobby"},
  {id: "dice", labelKey: "categoryDice", defaultLabel: "Dados", icon: "🎲", path: "/games/category/dice"},
  {id: "fishing", labelKey: "categoryFishing", defaultLabel: "Pesca", icon: "🎣", path: "/games/category/fishing"},
  {
    id: "virtual-sports",
    labelKey: "categoryVirtualSports",
    defaultLabel: "Virtual Sports",
    icon: "🏟️",
    path: "/games/category/virtual_sports",
  },
  {
    id: "table-games",
    labelKey: "categoryTableGames",
    defaultLabel: "Juegos de mesa",
    icon: "♠️",
    pillFilter: {filterClasses: ["table", "table_game"]},
  },
  {
    id: "domino",
    labelKey: "categoryDomino",
    defaultLabel: "Dominó",
    icon: "🁫",
    path: "/games/category/domino",
  },
  {
    id: "poker",
    labelKey: "categoryPoker",
    defaultLabel: "Poker",
    icon: "♦️",
    path: "/games/evenbet/pragmaticplayPoker",
    requiresAuth: true,
  },
];


export const CategoryPills = () => {
  const {t} = useTranslation();
  const navigate = useNavigate();
  const setStoreSearch = useMiniGames(state => state.setSearch);
  const authStatus = useAuthStore(state => state.authStatus);
  const handleShowAuthModal = useAuthStore(state => state.handleShowAuthModal);

  const handleClick = useCallback(
    category => {
      if (category.requiresAuth && authStatus !== "authenticated") {
        handleShowAuthModal();
        return;
      }
      if (category.scrollTo) {
        const el = document.getElementById(category.scrollTo);
        if (el) {
          el.scrollIntoView({behavior: "smooth", block: "center"});
          return;
        }
      }

      if (category.pillFilter) {
        setStoreSearch("");
        navigate("/gameslist/all#all-games", {
          state: {
            fromCategoryPillAt: Date.now(),
            pillFilter: category.pillFilter,
          },
        });
        return;
      }

      if (category.slotsNavigation) {
        setStoreSearch("");
        navigate(SLOTS_GAMES_NAVIGATION.pathname);
        return;
      }

      if (category.path) {
        navigate(category.path);
      }
    },
    [authStatus, handleShowAuthModal, navigate, setStoreSearch]
  );

  return (
    <section className={styles.wrapper}>
      <p className={styles.title}>
        <span
          className={styles.titleIcon}
          aria-hidden="true"
        >
          ✨
        </span>
        <span className={styles.titleText}>{t("gameSearchCategories", {defaultValue: "Categorías"})}</span>
      </p>
      <nav aria-label={t("categoriesNavLabel", {defaultValue: "Categorías de juegos"})}>
        <ul className={styles.list}>
          {GAME_CATEGORIES.map(category => (
            <li key={category.id}>
              <button
                type="button"
                className={styles.pill}
                onClick={() => handleClick(category)}
                aria-label={t(category.labelKey, {defaultValue: category.defaultLabel})}
              >
                <span
                  className={styles.pillIcon}
                  aria-hidden="true"
                >
                  {category.icon}
                </span>
                <span className={styles.pillLabel}>{t(category.labelKey, {defaultValue: category.defaultLabel})}</span>
              </button>
            </li>
          ))}
        </ul>
      </nav>
    </section>
  );
};